// ABOUTME: The device and session ids app.simera.me sends with every API call (x-device-id, x-session-id),
// ABOUTME: so a user's session can be rebuilt offline, signed in or not (architecture/identity-and-funnel.md §8).
import { getTraceId } from '$store/utils/traceId';

/** A visit ends after this long without an API call. */
export const SESSION_IDLE_MS = 30 * 60 * 1000;

const DEVICE_KEY = 'traceId'; // the per-browser id the app already keeps; the request log knows it as x-trace-id
const SESSION_KEY = 'sessionId';

// Storage can be blocked (private mode, policies); the ids then live for the page's lifetime.
const memory: { device?: string; session?: { id: string; lastSeen: number } } =
  {};

// An analytics id, not a secret: crypto when the browser has it, and never a reason for a page to fail.
const newId = (): string => {
  const { crypto } = globalThis;
  if (crypto?.randomUUID) return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (crypto?.getRandomValues) crypto.getRandomValues(bytes);
  else
    bytes.forEach((_, i) => {
      bytes[i] = Math.floor(Math.random() * 256);
    });
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

export const getDeviceId = (): string => {
  try {
    const existing = getTraceId();
    if (existing) return existing;
    const id = newId();
    localStorage.setItem(DEVICE_KEY, id);
    return id;
  } catch {
    memory.device = memory.device ?? newId();
    return memory.device;
  }
};

/** The current visit's id; every call counts as activity, so it rotates only after SESSION_IDLE_MS idle. */
export const getSessionId = (now: number = Date.now()): string => {
  let stored = memory.session;
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    stored = raw ? JSON.parse(raw) : undefined;
  } catch {
    // keep the in-memory session
  }
  const session =
    stored &&
    typeof stored.id === 'string' &&
    now - stored.lastSeen <= SESSION_IDLE_MS
      ? { id: stored.id, lastSeen: now }
      : { id: newId(), lastSeen: now };
  memory.session = session;
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // in memory only
  }
  return session.id;
};

export const sessionHeaders = (): Record<string, string> => ({
  'x-device-id': getDeviceId(),
  'x-session-id': getSessionId(),
});

const isSimeraApi = (url?: string, baseURL?: string): boolean => {
  const api = import.meta.env.REACT_APP_API_URL;
  if (!api || !url) return false;
  try {
    const target = new URL(url, baseURL ?? window.location.origin);
    const apiUrl = new URL(api);
    return target.origin === apiUrl.origin;
  } catch {
    return false;
  }
};

/** axios request interceptor: only calls to the Simera API get the ids; S3 uploads and third parties stay as built. */
export const addSessionHeaders = <
  T extends { url?: string; baseURL?: string; headers?: any },
>(
  config: T
): T => {
  if (!isSimeraApi(config.url, config.baseURL)) return config;
  const headers = sessionHeaders();
  Object.entries(headers).forEach(([name, value]) => {
    if (typeof config.headers?.set === 'function')
      config.headers.set(name, value);
    else config.headers = { ...config.headers, [name]: value };
  });
  return config;
};
